<template>
  <div class="purchase-view-page">
    <v-card v-if="isLoading" outlined class="loading-card">
      <v-card-text class="pa-6 text-center">
        <v-progress-circular indeterminate color="primary" />
        <div class="loading-text mt-3">Loading purchase...</div>
      </v-card-text>
    </v-card>

    <template v-else-if="Purchase && Purchase.Vendor && currentBusiness">
      <v-card outlined class="purchase-document-card">
        <PrintViewBar
          class="no-print"
          :showEmailBtn="showEmailBtn"
          :id="Purchase.id"
          type="purchase"
          :title="`Purchase # ${Purchase.localId}`"
          :postfix="`view?key=${currentBusiness.code}`"
          :name="currentBusiness.name"
          :email="Purchase.Vendor.email || ''"
          :obj="Purchase.Vendor"
          :text="`${currentBusiness.name} Purchase click here to view`"
        />

        <div class="document-header">
          <div>
            <div class="document-eyebrow">Inventory Purchase</div>
            <div class="document-title">Purchase #{{ Purchase.localId }}</div>
            <div class="document-date">
              {{ Purchase.date | moment("MMMM D, YYYY") }}
            </div>
          </div>

          <div class="header-side">
            <v-chip
              small
              :color="purchaseDue === 0 ? 'green lighten-5' : 'orange lighten-5'"
              :text-color="purchaseDue === 0 ? 'green darken-3' : 'orange darken-3'"
              class="font-weight-bold"
            >
              {{ purchaseDue === 0 ? "Paid" : "Open" }}
            </v-chip>
            <img
              v-if="currentBusiness.imageUrl"
              class="business-logo"
              :src="currentBusiness.imageUrl"
              alt="Business logo"
            />
          </div>
        </div>

        <div class="status-stamp" v-if="!$vuetify.breakpoint.xsOnly">
          <img v-if="Purchase.due === 0" src="../../../../assets/paidStamp.png" />
          <img v-if="Purchase.deletedAt" src="../../../../assets/deletedStamp-removebg-preview.png" />
        </div>

        <v-card-text class="pa-4 pa-sm-5">
          <div class="information-grid">
            <div class="information-card">
              <div class="info-heading">
                <v-icon small color="primary">mdi-office-building-outline</v-icon>
                From
              </div>
              <div class="info-title">{{ currentBusiness.name }}</div>
              <div v-if="currentBusiness.address" class="info-line">
                {{ currentBusiness.address }} {{ currentBusiness.city }},
                {{ currentBusiness.state }} {{ currentBusiness.zip }}
              </div>
              <div v-if="currentBusiness.phoneNumber" class="info-line">
                {{ currentBusiness.phoneNumber }}
              </div>
              <div v-if="currentBusiness.email" class="info-line text-lowercase">
                {{ currentBusiness.email }}
              </div>
            </div>

            <div class="information-card">
              <div class="info-heading">
                <v-icon small color="primary">mdi-storefront-outline</v-icon>
                Vendor
              </div>
              <div class="info-title">{{ Purchase.Vendor.name }}</div>
              <div v-if="Purchase.Vendor.address" class="info-line">
                {{ Purchase.Vendor.address }}
              </div>
              <div v-if="Purchase.Vendor.city" class="info-line">
                {{ Purchase.Vendor.city }}, {{ Purchase.Vendor.state }}
                {{ Purchase.Vendor.zip }}
              </div>
              <div v-if="Purchase.Vendor.email" class="info-line text-lowercase">
                {{ Purchase.Vendor.email }}
              </div>
            </div>

            <div class="information-card purchase-meta-card">
              <div class="meta-row"><span>Purchase number</span><strong>{{ Purchase.localId }}</strong></div>
              <div class="meta-row"><span>Reference</span><strong>{{ Purchase.referenceNumber || "--" }}</strong></div>
              <div class="meta-row">
                <span>Created by</span>
                <strong>{{ creatorName }}</strong>
              </div>
              <div v-if="Purchase.Vendor.showBalance" class="meta-row vendor-balance">
                <span>Vendor balance</span>
                <strong>{{ Purchase.Vendor.balance | currency }}</strong>
              </div>
            </div>
          </div>

          <v-alert v-if="Purchase.note" text type="info" dense class="purchase-note">
            <strong>Note:</strong> {{ Purchase.note }}
          </v-alert>

          <div class="items-header">
            <div>
              <div class="section-title">Purchase Items</div>
              <div class="section-subtitle">
                {{ Purchase.PurchaseDetails.length }} line{{ Purchase.PurchaseDetails.length === 1 ? "" : "s" }}
              </div>
            </div>
            <div class="quantity-pill">{{ totalQty }} total quantity</div>
          </div>

          <v-data-table
            dense
            sort-by="id"
            :mobile-breakpoint="0"
            :headers="headers"
            :items-per-page="Purchase.PurchaseDetails.length || 1"
            hide-default-footer
            :items="Purchase.PurchaseDetails"
            class="purchase-items-table"
          >
            <template v-slot:item="{ item }">
              <tr>
                <td class="text-left font-weight-medium">{{ item.name }}</td>
                <td class="text-center">{{ item.totalQty }}</td>
                <td class="text-right">{{ item.cost | currency }}</td>
                <td class="text-right font-weight-bold">
                  {{ (item.cost * item.totalQty) | currency }}
                </td>
              </tr>
            </template>
          </v-data-table>

          <div class="document-bottom">
            <div class="signature-panel">
              <div class="item-count" @click="showCost = !showCost">
                {{ totalQty }} ITEM(S)
              </div>
              <div class="signature-line">Signature</div>
            </div>

            <div class="totals-card">
              <div class="total-row"><span>Subtotal</span><strong>{{ Purchase.total | currency }}</strong></div>
              <div
                v-for="(payment, index) in paymentDetails"
                :key="index"
                class="total-row payment-row"
              >
                <span>{{ payment.createdAt | moment("MM/DD/YYYY") }}</span>
                <strong>-{{ payment.amount | currency }}</strong>
              </div>
              <div class="total-row"><span>Total paid</span><strong class="red--text">-{{ totalPayment | currency }}</strong></div>
              <div class="total-row due-row"><span>Purchase due</span><strong>{{ purchaseDue | currency }}</strong></div>
            </div>
          </div>

          <div
            v-if="currentBusiness.metadata && currentBusiness.metadata.openPurchaseListOnPurchase && OpenPurchases.length"
            class="open-purchases"
          >
            <div class="section-title">Open Purchases</div>
            <OpenPurchaseList class="mt-3" :Purchase="OpenPurchases" />
          </div>

          <div v-if="currentBusiness.policy" class="policy-card">
            <div class="policy-title">Policy</div>
            <div class="policy-text">{{ currentBusiness.policy }}</div>
          </div>

          <div class="powered-by">
            <img width="92" src="@/assets/images/poweredbylogo.png" />
          </div>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
import PrintViewBar from "@/components/PrintViewBar";
import OpenPurchaseList from "@/modules/Inventory/Purchase/components/OpenPurchaseList.vue";
import purchaseService from "@/modules/Inventory/Purchase/service.js";
import Hashids from "hashids";
import { mapGetters, mapActions } from "vuex";
const hashids = new Hashids();

export default {
  name: "PurchaseView",
  components: { PrintViewBar, OpenPurchaseList },
  data() {
    return {
      totalPayment: 0,
      OpenPurchases: [],
      showCost: false,
      showEmailBtn: false,
      isLoading: true,
      Purchase: { PurchaseDetails: [], PaymentDetails: [], Vendor: null },
      totalQty: 0,
      totalBoxes: 0,
    };
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    headers() {
      return [
        { text: "ITEM NAME", align: "left", value: "name", sortable: false },
        {
          text: this.currentBusiness && this.currentBusiness.metadata && this.currentBusiness.metadata.boxCountOnInventory ? "BOXES" : "QTY",
          align: "center", value: "totalQty", width: 90, sortable: false,
        },
        { text: "PRICE", align: "right", value: "cost", width: 130, sortable: false },
        { text: "TOTAL", align: "right", value: "total", width: 150, sortable: false },
      ];
    },
    creatorName() {
      if (this.Purchase.SalesRep) return this.Purchase.SalesRep.name;
      return this.Purchase.User ? this.Purchase.User.name : "--";
    },
    paymentDetails() {
      return this.Purchase.PaymentDetails || this.Purchase.PurchasePaymentDetails || [];
    },
    purchaseDue() {
      return Number(this.Purchase.total || 0) - Number(this.totalPayment || 0);
    },
  },
  async created() {
    if (this.$route.meta.public) {
      await this.loadPublicData();
      return;
    }
    if (!this.currentBusiness) await this.initUser();
    await this.loadData();
  },
  methods: {
    ...mapActions("global", ["initUser", "initBusiness"]),
    decodedId() {
      const decoded = hashids.decode(this.$route.params.id);
      return decoded.length ? decoded[0] : null;
    },
    applyPurchase(purchase) {
      this.Purchase = purchase;
      this.showEmailBtn = Boolean(purchase.Vendor && purchase.Vendor.email);
      this.totalQty = (purchase.PurchaseDetails || []).reduce(
        (sum, row) => sum + Math.abs(Number(row.totalQty || row.qty || 0)), 0
      );
      this.totalBoxes = (purchase.PurchaseDetails || []).reduce(
        (sum, row) => sum + Math.abs(Number(row.totalBoxes || row.boxQty || 0)), 0
      );
      this.totalPayment = this.paymentDetails.reduce(
        (sum, row) => sum + Number(row.amount || 0), 0
      );
      document.title = `Purchase # ${purchase.localId}`;
    },
    async loadPublicData() {
      try {
        const response = await purchaseService.getPurchaseByIdInPublic(this.decodedId(), {
          purchase: true,
          key: this.$route.query.key,
        });
        this.initBusiness(response.business);
        this.applyPurchase(response.data);
        this.$Progress.finish();
        await this.loadOpenPurchases();
      } finally {
        this.isLoading = false;
      }
    },
    async loadData() {
      try {
        const response = await purchaseService.getById(this.decodedId(), { purchase: true });
        this.applyPurchase(response.data);
        await this.loadOpenPurchases();
      } finally {
        this.isLoading = false;
      }
    },
    async loadOpenPurchases() {
      const metadata = this.currentBusiness && this.currentBusiness.metadata;
      if (!metadata || !metadata.openPurchaseListOnPurchase || !this.Purchase.VendorId) return;
      const response = await purchaseService.getAll({ VendorId: this.Purchase.VendorId, due: true });
      this.OpenPurchases = response.data || [];
    },
  },
};
</script>

<style scoped>
.purchase-view-page{background:#f8fafc;min-height:100%;padding:12px}.loading-card,.purchase-document-card{max-width:1100px;margin:0 auto;border-radius:22px;background:#fff;overflow:hidden}.loading-text{color:#64748b;font-size:13px}.document-header{padding:22px 24px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;gap:18px}.document-eyebrow{color:#2563eb;font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}.document-title{color:#0f172a;font-size:27px;font-weight:900;line-height:1.2;margin-top:3px}.document-date{color:#64748b;font-size:13px;margin-top:4px}.header-side{display:flex;align-items:center;gap:14px}.business-logo{max-width:120px;max-height:64px;object-fit:contain}.status-stamp{position:absolute;left:43%;top:115px;z-index:1;pointer-events:none}.status-stamp img{width:210px;opacity:.65}.information-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px}.information-card{background:#f8fafc;border-radius:16px;padding:15px}.info-heading{display:flex;align-items:center;gap:6px;color:#64748b;font-size:11px;font-weight:800;text-transform:uppercase}.info-title{color:#0f172a;font-size:16px;font-weight:900;margin-top:9px}.info-line{color:#64748b;font-size:12px;line-height:1.55}.meta-row{display:flex;justify-content:space-between;gap:12px;padding:7px 0;border-bottom:1px solid #e2e8f0;color:#64748b;font-size:12px}.meta-row:last-child{border-bottom:0}.meta-row strong{color:#0f172a;text-align:right}.vendor-balance strong{color:#2563eb}.purchase-note{margin:16px 0 0}.items-header{display:flex;align-items:center;justify-content:space-between;gap:14px;margin:22px 0 10px}.section-title{color:#0f172a;font-size:17px;font-weight:900}.section-subtitle{color:#64748b;font-size:11px}.quantity-pill{background:#eff6ff;border-radius:12px;color:#2563eb;font-size:11px;font-weight:800;padding:8px 11px}.purchase-items-table{border:1px solid #e2e8f0;border-radius:16px;overflow:hidden}.document-bottom{display:grid;grid-template-columns:1fr minmax(290px,390px);gap:30px;margin-top:20px}.item-count{color:#475569;font-size:12px;font-weight:800}.signature-line{max-width:230px;border-top:1px solid #94a3b8;color:#64748b;font-size:11px;margin-top:74px;padding-top:5px}.totals-card{background:#f8fafc;border-radius:16px;padding:13px 15px}.total-row{display:flex;justify-content:space-between;gap:15px;padding:8px 0;color:#64748b;font-size:12px;border-bottom:1px solid #e2e8f0}.total-row strong{color:#0f172a}.payment-row strong{color:#dc2626}.due-row{border-bottom:0;font-size:15px;font-weight:900}.due-row span,.due-row strong{color:#0f172a}.open-purchases{margin-top:24px}.policy-card{background:#f8fafc;border-radius:14px;padding:14px;margin-top:22px}.policy-title{color:#0f172a;font-size:12px;font-weight:900;text-transform:uppercase}.policy-text{color:#64748b;font-size:11px;line-height:1.5;margin-top:5px}.powered-by{text-align:right;margin-top:18px}@media(max-width:760px){.purchase-view-page{padding:6px}.document-header{padding:17px}.document-title{font-size:23px}.business-logo{max-width:82px}.information-grid{grid-template-columns:1fr}.document-bottom{grid-template-columns:1fr}.signature-line{margin-top:40px}.items-header{align-items:flex-start;flex-direction:column}.status-stamp{display:none}}
@media print{.purchase-view-page{padding:0;background:#fff}.purchase-document-card{border:0!important;max-width:none}.no-print{display:none!important}*{box-shadow:none!important}.information-card,.totals-card,.policy-card{background:#fff!important;border:1px solid #ddd}.status-stamp img{opacity:.45}}
</style>
